import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import {
  fetchPaymentMethods,
  openWompiCheckoutPlaceholder,
  navigateWompiCheckout,
} from '../../utils/paymentMethods';
import { buildWaMeUrl } from '../../utils/whatsappSupport';
import { translatePaymentMethod } from '../../utils/i18nHelpers';
import { isCoachClientUser } from '../dashboard/userServices';
import { Apple, Dumbbell, Radio } from 'lucide-react';
import {
  canPurchaseFoodAddon,
  coachFoodAccountStatus,
  hasExpiredFoodAccess,
  hasPaidFoodAccess,
  hasPendingFoodAccess,
} from '../../utils/foodAccess';

const ADDON_DEFS = [
  {
    id: 'd28d',
    moduleCode: 'd28d',
    kind: 'd28d',
    titleKey: 'support.addon.d28d_title',
    titleFallback: 'Fit Live · Programa',
    descKey: 'support.addon.d28d_desc',
    descFallback: 'Clases en vivo, comunidad y ciclos del programa.',
    icon: Radio,
    accessKeys: ['d28d', 'live_classes', 'gym'],
    licenseCodes: ['d28d', 'live_classes', 'gym'],
  },
  {
    id: 'food',
    moduleCode: 'food',
    kind: 'food',
    titleKey: 'support.addon.food_title',
    titleFallback: 'FOOD PLAN',
    descKey: 'support.addon.food_desc',
    descFallback: 'Plan nutricional guiado, registro de alimentos y seguimiento con tu equipo.',
    icon: Apple,
    accessKeys: ['food_plan', 'nutrition'],
    licenseCodes: ['food'],
  },
  {
    id: 'training',
    moduleCode: 'training',
    kind: 'training',
    titleKey: 'support.addon.training_title',
    titleFallback: 'Entrenamiento',
    descKey: 'support.addon.training_desc',
    descFallback: 'Rutinas personalizadas, seguimiento con coach y progreso de entrenamiento.',
    icon: Dumbbell,
    accessKeys: ['training'],
    licenseCodes: ['training'],
  },
];

function hasActiveAddon(user, myServices, addon) {
  if (addon.id === 'food') {
    return hasPaidFoodAccess(user, myServices);
  }
  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  const codes = addon.licenseCodes || [addon.moduleCode];
  if (licenses.some((lic) => lic.active !== false && codes.includes(lic.module_code))) {
    return true;
  }
  if (licenses.length) return false;
  const svc = myServices.find((s) => s.service === addon.id);
  if (svc && ACTIVE_SERVICE_STATUSES.has(String(svc.status || '').toLowerCase())) {
    return true;
  }
  const ma = user?.module_access || {};
  const accessKeys = addon.accessKeys || [];
  if (accessKeys.some((k) => ma[k])) {
    if (svc && ['vencido', 'cancelado'].includes(String(svc.status || '').toLowerCase())) return false;
    return true;
  }
  return false;
}

const ACTIVE_SERVICE_STATUSES = new Set(['activo', 'vigente']);

function planDisplayName(plan, fallbackTitle) {
  if (!plan) return fallbackTitle;
  return plan.display_nombre || plan.display_name || fallbackTitle || String(plan.nombre || '')
    .replace(/_/g, ' ');
}

function detectServiceProfile(user, myServices) {
  const byId = (id) => ADDON_DEFS.find((a) => a.id === id);
  return {
    food: hasActiveAddon(user, myServices, byId('food')),
    training: hasActiveAddon(user, myServices, byId('training')),
    d28d: hasActiveAddon(user, myServices, byId('d28d')),
  };
}

/** Cross-selling: FOOD→TRAINING+D28D, TRAINING→FOOD+D28D, D28D→FOOD+TRAINING; multiservicio→solo faltantes. */
function crossSellAddonIds(user, myServices) {
  const { food, training, d28d } = detectServiceProfile(user, myServices);
  const activeCount = [food, training, d28d].filter(Boolean).length;
  let allowed;
  if (activeCount >= 2) {
    allowed = ['d28d', 'food', 'training'];
  } else if (food && !training && !d28d) {
    allowed = ['training', 'd28d'];
  } else if (training && !food && !d28d) {
    allowed = ['food', 'd28d'];
  } else if (d28d && !food && !training) {
    allowed = ['food', 'training'];
  } else {
    allowed = ['d28d', 'food', 'training'];
  }
  return allowed.filter((id) => {
    const addon = ADDON_DEFS.find((a) => a.id === id);
    return addon && !hasActiveAddon(user, myServices, addon);
  });
}

export default function AddonServicesPanel({
  compact = false,
  alwaysVisible = false,
  excludeTraining = false,
  excludeD28d = false,
  allowedAddonIds = null,
  hideSubtitle = false,
  titleOverride = null,
}) {
  const { user, refreshProfile } = useAuth();
  const { t } = useI18n();
  const [plansByKind, setPlansByKind] = useState({ d28d: [], food: [], training: [] });
  const [myServices, setMyServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [metodoPago, setMetodoPago] = useState('wompi_online');
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlanByAddon, setSelectedPlanByAddon] = useState({ d28d: null, food: null, training: null });
  const [formError, setFormError] = useState('');

  const coachClient = isCoachClientUser(user);
  const foodPending = hasPendingFoodAccess(user, myServices);

  const resolveSellIds = useCallback((services) => {
    let ids;
    if (Array.isArray(allowedAddonIds) && allowedAddonIds.length) {
      ids = allowedAddonIds.filter((id) => {
        if (id === 'food' && !canPurchaseFoodAddon(user, services)) return false;
        const addon = ADDON_DEFS.find((a) => a.id === id);
        return addon && !hasActiveAddon(user, services, addon);
      });
    } else {
      ids = crossSellAddonIds(user, services);
    }
    if (excludeD28d) ids = ids.filter((id) => id !== 'd28d');
    if (excludeTraining) ids = ids.filter((id) => id !== 'training');
    return ids;
  }, [user, allowedAddonIds, excludeD28d, excludeTraining]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const svcRes = await api.get('/accounts/my-services').catch(() => ({ data: { services: [] } }));
      const services = svcRes.data?.services || [];
      setMyServices(services);
      const sellIds = resolveSellIds(services);
      const kindsNeeded = [...new Set(
        sellIds.map((id) => ADDON_DEFS.find((a) => a.id === id)?.kind).filter(Boolean),
      )];
      const planResults = await Promise.all(
        kindsNeeded.map(async (kind) => {
          const params = { kind, visible: 'true' };
          if (kind === 'food') {
            params.platform_only = 'true';
          } else {
            const userGymId = user?.gym_id ?? user?.gymId ?? null;
            if (userGymId) params.gym_id = userGymId;
            else params.platform_only = 'true';
          }
          let res = await api.get('/accounts/plans', { params });
          let list = Array.isArray(res.data) ? res.data.filter((p) => p.activo !== false) : [];
          if (kind === 'food' && !list.length) {
            const fallback = await api.get('/accounts/plans', { params: { kind: 'food', visible: 'true' } });
            const all = Array.isArray(fallback.data) ? fallback.data.filter((p) => p.activo !== false) : [];
            list = all.filter((p) => p.gym_id == null);
          }
          return { kind, list };
        }),
      );
      const nextPlans = { d28d: [], food: [], training: [] };
      planResults.forEach(({ kind, list }) => {
        nextPlans[kind] = list;
      });
      setPlansByKind(nextPlans);
      setSelectedPlanByAddon({
        d28d: nextPlans.d28d[0]?.nombre || null,
        food: nextPlans.food[0]?.nombre || null,
        training: nextPlans.training[0]?.nombre || null,
      });
    } catch {
      setPlansByKind({ d28d: [], food: [], training: [] });
      setMyServices([]);
    } finally {
      setLoading(false);
    }
  }, [user, resolveSellIds]);

  useEffect(() => { load(); }, [load]);

  const availableAddons = useMemo(
    () => {
      const sellIds = resolveSellIds(myServices);
      return ADDON_DEFS.filter((addon) => sellIds.includes(addon.id));
    },
    [myServices, resolveSellIds],
  );

  const consumerPaymentMethods = useMemo(() => {
    const raw = paymentMethods.length
      ? paymentMethods
      : [
        { id: 'wompi_online', label: t('payment.wompi_online', 'Pago en línea (Wompi)') },
        { id: 'pago_sede', label: t('payment.pago_sede', 'Pago en sede') },
      ];
    // Usuario final: no ofrecer activación manual (solo ops/admin).
    return raw.filter((m) => m.id !== 'pago_pendiente');
  }, [paymentMethods, t]);

  const openModal = async (addon, plan) => {
    setFormError('');
    const methods = await fetchPaymentMethods(addon.moduleCode);
    const filtered = (methods || []).filter((m) => m.id !== 'pago_pendiente');
    setPaymentMethods(filtered);
    setMetodoPago(filtered[0]?.id || 'wompi_online');
    setModal({ addon, plan });
  };

  const completeAddonCheckout = async ({
    planNombre,
    moduleCode,
    payMethod = 'wompi_online',
  }) => {
    const wompiWin = payMethod === 'wompi_online' ? openWompiCheckoutPlaceholder() : null;
    setSubmitting(true);
    setFormError('');
    try {
      const res = await api.post('/accounts', {
        plan: planNombre,
        metodoPago: payMethod,
        module_code: moduleCode,
        gym_id: user?.gym_id || null,
        trainer_id: user?.trainer_id || null,
      });
      const payload = res.data || {};
      if (payMethod === 'wompi_online') {
        if (!payload.payment_url) {
          try { wompiWin?.close?.(); } catch { /* noop */ }
          const msg = t(
            'support.addon.wompi_url_missing',
            'No se recibió el enlace de Wompi. Revisa los links de pago del módulo o contacta soporte.',
          );
          setFormError(msg);
          emitToast({
            type: 'error',
            title: t('support.addon.error_title', 'No se pudo contratar'),
            message: msg,
          });
          return false;
        }
        navigateWompiCheckout(wompiWin, payload.payment_url);
      } else {
        try { wompiWin?.close?.(); } catch { /* noop */ }
      }
      emitToast({
        type: 'success',
        title: payload.pending
          ? t('support.addon.pending_title', 'Solicitud registrada')
          : t('support.addon.success_title', 'Servicio contratado'),
        message: payload.pending
          ? (payMethod === 'wompi_online'
            ? (String(moduleCode) === 'food'
              ? t(
                'support.addon.pending_food_wompi_msg',
                'Completa el pago en Wompi. Tu entrenador activará FOOD PLAN cuando reciba el dinero.',
              )
              : t('support.addon.pending_wompi_msg', 'Completa el pago en la pestaña de Wompi. Al confirmarse se activará el servicio.'))
            : (moduleCode === 'food'
              ? t(
                'support.addon.pending_food_sede_msg',
                'Solicitud registrada. Un super administrador confirmará el pago en sede y activará FOOD PLAN (independiente de Fit Live · Programa).',
              )
              : t('support.addon.pending_msg', 'Un administrador confirmará tu pago y activará el servicio.')))
          : t('support.addon.success_msg', 'Tu nuevo servicio quedará disponible en unos segundos.'),
      });
      setModal(null);
      await refreshProfile();
      await load();
      return true;
    } catch (err) {
      try { wompiWin?.close?.(); } catch { /* noop */ }
      const msg = err.response?.data?.error || t('support.addon.error_msg', 'Intenta de nuevo o contacta soporte.');
      setFormError(msg);
      emitToast({
        type: 'error',
        title: t('support.addon.error_title', 'No se pudo contratar'),
        message: msg,
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!modal?.plan) return;
    const isCoachFood = coachClient && modal.addon.id === 'food';
    const payMethod = isCoachFood ? 'wompi_online' : metodoPago;
    await completeAddonCheckout({
      planNombre: modal.plan.nombre,
      moduleCode: modal.addon.moduleCode,
      payMethod,
    });
  };

  const handleCompletePendingFood = async () => {
    const foodPlan = plansByKind.food[0]
      || { nombre: myServices.find((s) => s.service === 'food')?.plan || 'FOOD_PLAN' };
    // Cargar plan food si no está (porque el upsell se ocultó al estar pendiente).
    let planNombre = foodPlan.nombre;
    if (!plansByKind.food.length) {
      try {
        const res = await api.get('/accounts/plans', { params: { kind: 'food', visible: 'true', platform_only: 'true' } });
        const list = Array.isArray(res.data) ? res.data.filter((p) => p.activo !== false) : [];
        planNombre = list[0]?.nombre || planNombre;
      } catch { /* use fallback */ }
    }
    await completeAddonCheckout({
      planNombre,
      moduleCode: 'food',
      payMethod: 'wompi_online',
    });
  };

  const contactAddon = (addon) => {
    const msgById = {
      d28d: t('support.addon.wa_d28d', 'Hola, quiero contratar Fit Live · Programa como servicio adicional.'),
      food: t('support.addon.wa_food', 'Hola, quiero contratar FOOD PLAN como servicio adicional.'),
      training: t('support.addon.wa_training', 'Hola, quiero contratar el módulo de Entrenamiento como servicio adicional.'),
    };
    window.open(buildWaMeUrl('573192635819', msgById[addon.id] || msgById.training), '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <p className="text-sm d28d-text-muted">{t('support.addon.loading', 'Cargando servicios adicionales…')}</p>;
  }

  if (!availableAddons.length) {
    const foodStatus = coachClient ? coachFoodAccountStatus(user, myServices) : null;
    if (alwaysVisible && (foodPending || foodStatus === 'pending')) {
      return (
        <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            {t('myaccount.food_pending_title', 'FOOD PLAN pendiente de pago')}
          </p>
          <p className="text-sm text-amber-800">
            {t(
              'myaccount.food_pending_hint',
              'Tu solicitud está registrada. Completa el pago en línea (Wompi) o espera la confirmación en sede para activar FOOD PLAN.',
            )}
          </p>
          <button
            type="button"
            className="btn-primary w-full"
            disabled={submitting}
            onClick={handleCompletePendingFood}
          >
            {submitting ? '…' : t('myaccount.food_complete_wompi', 'Completar pago en Wompi')}
          </button>
        </div>
      );
    }
    if (alwaysVisible && coachClient && foodStatus === 'active') {
      return (
        <div className="space-y-2 rounded-xl border border-lime-300 bg-lime-50 p-4">
          <p className="text-sm font-semibold text-lime-900">
            {t('myaccount.food_active_title', 'FOOD PLAN activo')}
          </p>
          <p className="text-sm text-lime-800">
            {t('myaccount.food_active_hint', 'Tu plan nutricional está vigente. Ábrelo desde Inicio o Mi Progreso → Alimentación.')}
          </p>
        </div>
      );
    }
    if (alwaysVisible && coachClient && foodStatus === 'none') {
      return (
        <p className="text-sm text-stone-600">
          {t('myaccount.food_not_contracted', 'Aún no tienes FOOD PLAN. Contrátalo aquí para registrar tu alimentación con tu coach.')}
        </p>
      );
    }
    if (alwaysVisible) {
      return (
        <div className="space-y-2">
          <p className="text-sm d28d-text-muted">
            {t('support.addon.all_active', 'Ya tienes activos los servicios complementarios disponibles.')}
          </p>
        </div>
      );
    }
    return compact ? null : (
      <p className="text-sm d28d-text-muted">
        {t('support.addon.all_active', 'Ya tienes activos los servicios complementarios disponibles.')}
      </p>
    );
  }

  const modalNode = modal ? (
    <div className="form-modal-overlay" onClick={() => !submitting && setModal(null)}>
      <div
        className="form-modal d28d-admin-modal ui-light-panel surface-readable w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-content space-y-4">
          <h3 className="text-xl font-bold">
            {modal.addon.id === 'food' && hasExpiredFoodAccess(user, myServices)
              ? t('support.addon.renew_food_title', 'Renovar FOOD PLAN')
              : (
                <>
                  {t('support.addon.checkout_title', 'Contratar')}{' '}
                  {t(modal.addon.titleKey, modal.addon.titleFallback)}
                </>
              )}
          </h3>
          <p className="form-modal-price text-xl font-extrabold">
            {planDisplayName(modal.plan, t(modal.addon.titleKey, modal.addon.titleFallback))}
            {' · '}${Number(modal.plan.precio_mensual || 0).toLocaleString('es-CO')} COP
          </p>
          <form onSubmit={handleSubscribe} className="space-y-3">
            {modal.addon.id === 'food' && hasExpiredFoodAccess(user, myServices) ? (
              <p className="text-sm d28d-text-muted">
                {t(
                  'support.addon.renew_food_note',
                  'Tu plan venció. Completa el pago; tu entrenador activará FOOD PLAN cuando reciba el dinero.',
                )}
              </p>
            ) : coachClient && modal.addon.id === 'food' ? (
              <p className="text-sm d28d-text-muted">
                {t('support.addon.coach_food_wompi', 'Completa el pago en Wompi. Tu entrenador activará FOOD PLAN cuando reciba el dinero.')}
              </p>
            ) : (
              <p className="text-sm d28d-text-muted">
                {t(
                  'support.addon.food_checkout_note',
                  'Completa el pago; tu entrenador activará FOOD PLAN con el mismo email de esta cuenta.',
                )}
              </p>
            )}
            {formError ? (
              <p className="alert-error text-sm" role="alert">{formError}</p>
            ) : null}
            <div>
              <p className="label mb-2">{t('myaccount.method', 'Método de pago')}</p>
              <div className="space-y-2">
                {(coachClient && modal.addon.id === 'food'
                  ? [{ id: 'wompi_online', label: t('payment.wompi_online', 'Pago en línea (Wompi)') }]
                  : consumerPaymentMethods
                ).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 p-2 rounded-lg border border-stone-300 cursor-pointer">
                    <input
                      type="radio"
                      name="addonMetodoPago"
                      value={m.id}
                      checked={metodoPago === m.id}
                      onChange={() => setMetodoPago(m.id)}
                    />
                    <span className="text-stone-900 font-medium normal-case tracking-normal">
                      {translatePaymentMethod(m.id, m.label, t)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" className="btn-secondary flex-1" disabled={submitting} onClick={() => setModal(null)}>
                {t('common.cancel', 'Cancelar')}
              </button>
              <button type="submit" className="btn-primary flex-1" disabled={submitting}>
                {submitting ? '…' : t('support.addon.confirm', 'Confirmar')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact ? (
        <div>
          <h2 className="font-bold text-[var(--d28d-text)] text-lg">
            {titleOverride || t('support.addon.title', 'Servicios adicionales')}
          </h2>
          {!hideSubtitle ? (
            <p className="text-sm d28d-text-muted mt-1">
              {allowedAddonIds?.length === 1 && allowedAddonIds[0] === 'food'
                ? t(
                  'support.addon.subtitle_food_d28d',
                  'Contrata FOOD PLAN como complemento. Elige plan y pago (Wompi o en sede). No se crea acceso hasta confirmar el pago.',
                )
                : t('support.addon.subtitle', 'Contrata nutrición, entrenamiento o Fit Live · Programa como complemento a tu plan actual.')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        {availableAddons.map((addon) => {
          const plans = plansByKind[addon.kind] || [];
          const selectedName = selectedPlanByAddon[addon.id] || plans[0]?.nombre;
          const plan = plans.find((p) => p.nombre === selectedName) || plans[0];
          const Icon = addon.icon;
          return (
            <div key={addon.id} className="card p-4 flex flex-col gap-3 border border-[var(--d28d-border)]">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-[var(--d28d-yellow)]/15 p-2 text-[var(--d28d-yellow)]">
                  <Icon className="w-5 h-5" aria-hidden />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--d28d-text)]">
                    {t(addon.titleKey, addon.titleFallback)}
                  </h3>
                  <p className="text-sm d28d-text-muted mt-1">
                    {t(addon.descKey, addon.descFallback)}
                  </p>
                </div>
              </div>
              {plan ? (
                <>
                  <p className="text-sm font-semibold text-[var(--d28d-yellow)]">
                    {planDisplayName(plan, t(addon.titleKey, addon.titleFallback))}
                    {' · '}
                    ${Number(plan.precio_mensual || 0).toLocaleString('es-CO')} COP/mes
                  </p>
                  {plans.length > 1 ? (
                    <label className="text-sm">
                      <span className="label block mb-1">{t('support.addon.pick_plan', 'Plan')}</span>
                      <select
                        className="input w-full"
                        value={selectedName || ''}
                        onChange={(e) => {
                          setSelectedPlanByAddon((prev) => ({ ...prev, [addon.id]: e.target.value }));
                        }}
                      >
                        {plans.map((p) => (
                          <option key={p.nombre} value={p.nombre}>
                            {planDisplayName(p, p.nombre)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <button
                    type="button"
                    className="btn-primary w-full mt-auto"
                    onClick={() => openModal(addon, plan)}
                    disabled={!plan}
                  >
                    {addon.id === 'food'
                      ? t('support.addon.subscribe_food', 'Adquirir y contratar')
                      : t('support.addon.subscribe', 'Contratar y pagar')}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-xs d28d-text-muted">
                    {t('support.addon.no_plan', 'Consulta con soporte para activar este servicio.')}
                  </p>
                  <button type="button" className="btn-secondary w-full" onClick={() => contactAddon(addon)}>
                    {t('support.addon.ask_whatsapp', 'Consultar por WhatsApp')}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {typeof document !== 'undefined' && modalNode
        ? createPortal(modalNode, document.body)
        : null}
    </div>
  );
}
